import axios from 'axios';
import { extractDomain, formatCompanyName } from './websiteAnalyzer';

// Rate limiting configuration
const RATE_LIMIT = {
  maxConcurrent: 2, // Lower limit for opener generation to prevent overload
  delayBetweenRequests: 1500, // 1.5 second delay between requests
  retryAttempts: 3,
  retryDelay: 2000 // 2 seconds initial retry delay
};

// Queue to manage concurrent requests
let activeSequentialRequests = 0;
let sequentialRequestQueue = [];

// Rate limiting helper functions
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const executeSequentialWithRateLimit = async (apiCall) => {
  return new Promise((resolve, reject) => {
    const execute = async () => {
      if (activeSequentialRequests >= RATE_LIMIT.maxConcurrent) {
        // Add to queue if at max concurrent requests
        sequentialRequestQueue.push(execute);
        return;
      }
      
      activeSequentialRequests++;
      
      try {
        const result = await apiCall();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        activeSequentialRequests--;
        
        // Process next request in queue after delay
        if (sequentialRequestQueue.length > 0) {
          setTimeout(() => {
            const nextRequest = sequentialRequestQueue.shift();
            nextRequest();
          }, RATE_LIMIT.delayBetweenRequests);
        }
      }
    };
    
    execute();
  });
};

const executeSequentialWithRetry = async (apiCall, retryCount = 0) => {
  try {
    return await executeSequentialWithRateLimit(apiCall);
  } catch (error) {
    if (retryCount < RATE_LIMIT.retryAttempts && 
        (error.response?.status === 500 || error.response?.status === 429 || error.code === 'ECONNRESET')) {
      console.log(`Sequential API call failed, retrying in ${RATE_LIMIT.retryDelay * (retryCount + 1)}ms... (attempt ${retryCount + 1}/${RATE_LIMIT.retryAttempts})`);
      await delay(RATE_LIMIT.retryDelay * (retryCount + 1)); // Exponential backoff
      return executeSequentialWithRetry(apiCall, retryCount + 1);
    }
    throw error;
  }
};

/**
 * Generates a personalized opener using a sequential, step-by-step approach
 * to ensure strict adherence to the 8-step workflow
 * 
 * @param {Object} websiteData - Website data including URL, company name, etc.
 * @param {Object} config - API configuration (apiKey, model, endpoint)
 * @param {Object} openerConfig - Optional opener generation settings
 * @returns {Promise<string>} - AI generated opener
 */
export const generateSequentialOpener = async (websiteData, config, openerConfig = null) => {
  try {
    // Prepare the context for the AI
    const companyName = formatCompanyName(websiteData.company, websiteData.url);
    const linkedInData = websiteData.linkedInData || null;
    const talkingPointsData = websiteData.talkingPointsData || null;
    const qualificationResult = websiteData.qualificationResult || null;

    // Apply opener configuration if provided
    const style = openerConfig?.style || 'casual';
    const length = openerConfig?.length || 'medium';
    const focus = openerConfig?.focus || 'refresh';
    const serviceType = openerConfig?.serviceType || 'website';
    const subServiceType = openerConfig?.subServiceType || null;
    const manualFocus = openerConfig?.manualFocus || '';
    const strategy = openerConfig?.strategy || '8step';

    // Create personalization talking points from scraped data
    let personalizationTalkingPoints = [];
    
    // Extract talking points from website data
    if (websiteData.title) {
      personalizationTalkingPoints.push(`Website focus: ${websiteData.title}`);
    }
    if (websiteData.headings) {
      const mainHeadings = websiteData.headings.split(',').slice(0, 2).join(', ');
      personalizationTalkingPoints.push(`Key offerings: ${mainHeadings}`);
    }
    
    // Extract talking points from LinkedIn data
    if (linkedInData) {
      if (linkedInData.headline) {
        personalizationTalkingPoints.push(`LinkedIn role: ${linkedInData.headline}`);
      }
      if (linkedInData.experience) {
        const recentExp = linkedInData.experience.substring(0, 100);
        personalizationTalkingPoints.push(`Recent experience: ${recentExp}`);
      }
    }
    
    // Extract talking points from business opportunities
    if (talkingPointsData && talkingPointsData.success && talkingPointsData.data?.opportunities) {
      const topOpportunities = talkingPointsData.data.opportunities.slice(0, 2);
      topOpportunities.forEach(opp => {
        personalizationTalkingPoints.push(`Business opportunity: ${opp}`);
      });
    }
    
    // Extract talking points from qualification analysis
    if (qualificationResult && qualificationResult.success) {
      personalizationTalkingPoints.push(`Prospect fit score: ${qualificationResult.fitScore}/10`);
      
      if (qualificationResult.keyIndicators && qualificationResult.keyIndicators.length > 0) {
        qualificationResult.keyIndicators.forEach(indicator => {
          personalizationTalkingPoints.push(`Key fit indicator: ${indicator}`);
        });
      }
      
      if (qualificationResult.concerns && qualificationResult.concerns.length > 0) {
        qualificationResult.concerns.forEach(concern => {
          personalizationTalkingPoints.push(`Potential concern to address: ${concern}`);
        });
      }
    }

    // Create service-specific context
    let serviceContext = getServiceContext(serviceType, subServiceType, focus, manualFocus);
    
    // Generate the opener using personalization talking points
    const opener = await generateStructuredOpener(
      websiteData,
      personalizationTalkingPoints,
      config,
      serviceContext,
      { style, length, focus, strategy },
      qualificationResult
    );
    
    return opener;
  } catch (error) {
    console.error('Error in sequential opener generation:', error);
    return `[Error generating opener: ${error.message}. Please check your API configuration and try again.]`;
  }
};

/**
 * Creates service-specific context based on service type and focus
 */
const getServiceContext = (serviceType, subServiceType, focus, manualFocus) => {
  let objective = '';
  let triggerType = '';
  let platformName = '';
  
  if (serviceType === 'website') {
    objective = "get a reply to share a one-pager showing website redesign examples";
    triggerType = "website content/offerings that need improvement";
    
    const focusDescriptions = {
      'refresh': 'content freshness and updating website information',
      'financing': '12-month financing options for website redesign',
      'conversion': 'conversion rate optimization and lead generation',
      'mobile': 'mobile responsiveness and user experience',
      'branding': 'brand consistency and messaging',
      'accessibility': 'website accessibility and compliance',
      'manual': manualFocus
    };
    
    return {
      serviceType,
      objective,
      triggerType,
      focusArea: focusDescriptions[focus] || focusDescriptions.refresh
    };
  } 
  else if (serviceType === 'onboarding') {
    platformName = subServiceType === 'apollo' ? 'Apollo.io' : 'Instantly.ai';
    objective = `get a reply to share how ${platformName} can specifically help their business`;
    triggerType = "sales or outreach challenges visible in their content";
    
    const focusDescriptions = subServiceType === 'apollo' ? {
      'prospecting': 'finding better-fit prospects',
      'engagement': 'engaging with prospects more effectively',
      'analytics': 'getting better insights from sales data',
      'integration': 'integrating with existing tools',
      'efficiency': 'automating repetitive prospecting tasks',
      'targeting': 'precise audience targeting',
      'manual': manualFocus
    } : {
      'deliverability': 'improving cold email deliverability',
      'automation': 'automating cold email outreach',
      'scaling': 'scaling cold email outreach efforts',
      'personalization': 'dynamic personalization at scale',
      'analytics': 'detailed email performance tracking',
      'compliance': 'maintaining email regulation compliance',
      'manual': manualFocus
    };
    
    return {
      serviceType,
      subServiceType,
      platformName,
      objective,
      triggerType,
      focusArea: focusDescriptions[focus] || (subServiceType === 'apollo' ? focusDescriptions.prospecting : focusDescriptions.deliverability)
    };
  } 
  else if (serviceType === 'cold_email') {
    objective = "get a reply to share a one-pager on cold email systems";
    triggerType = "lead generation challenges visible in their content";
    
    const focusDescriptions = {
      'good_way_to_supplement_leads': 'supplementing existing lead generation',
      'targeting': 'reaching specific audience segments',
      'high_roi': 'measurable ROI for their business model',
      'automation': 'saving time with cold email automation',
      'personalization': 'personalizing outreach at scale',
      'analytics': 'tracking lead performance',
      'manual': manualFocus
    };
    
    return {
      serviceType,
      objective,
      triggerType,
      focusArea: focusDescriptions[focus] || focusDescriptions.good_way_to_supplement_leads
    };
  }
  else if (serviceType === 'aeo') {
    objective = "get a reply to share insights on AI Engine Optimization (AEO) for their website";
    triggerType = "website content that could be optimized for AI search engines and chatbots";
    
    const focusDescriptions = {
      'ai_visibility': 'improving visibility in AI search results and chatbot recommendations',
      'content_structure': 'structuring content for AI consumption and understanding',
      'semantic_optimization': 'semantic markup and entity optimization for AI engines',
      'voice_search': 'optimizing for voice search and AI assistant responses',
      'ai_snippets': 'optimizing for AI-generated snippets and featured content',
      'chatbot_optimization': 'making content discoverable by AI chatbots',
      'manual': manualFocus
    };
    
    return {
      serviceType,
      objective,
      triggerType,
      focusArea: focusDescriptions[focus] || focusDescriptions.ai_visibility
    };
  }
  else if (serviceType === 'partnership') {
    objective = "get a reply to share a case study about a previous partnership";
    triggerType = "agencies or businesses that might need web development team support";
    
    const focusDescriptions = {
      'scaling': 'helping agencies scale without hiring overhead',
      'expertise': 'providing specialized technical skills and expertise',
      'capacity': 'handling overflow work during busy periods',
      'cost_efficiency': 'cost-effective alternative to building in-house teams',
      'client_retention': 'maintaining client satisfaction through reliable development',
      'time_to_market': 'accelerating project completion and delivery',
      'website_management': 'ongoing website management and maintenance services',
      'manual': manualFocus
    };
    
    return {
      serviceType,
      objective,
      triggerType,
      focusArea: focusDescriptions[focus] || focusDescriptions.scaling
    };
  }
  
  return { serviceType, objective, triggerType, focusArea: 'general improvement' };
};

// Removed analyzeWebsiteContent function - now using personalization talking points approach

/**
 * Generate structured opener using personalization talking points
 */
const generateStructuredOpener = async (websiteData, personalizationTalkingPoints, config, serviceContext, styleOptions, qualificationResult = null) => {
  try {
    const { style, length, focus, strategy } = styleOptions;
    const companyName = formatCompanyName(websiteData.company, websiteData.url);
    
    // Format personalization talking points for the prompt
    let talkingPointsArray = [...personalizationTalkingPoints];
    
    // Add qualification analysis data if available
    if (qualificationResult && qualificationResult.success) {
      talkingPointsArray.push(`Prospect Qualification Analysis:`);
      talkingPointsArray.push(`- Fit Score: ${qualificationResult.fitScore}/10`);
      
      if (qualificationResult.reasoning) {
        talkingPointsArray.push(`- Analysis: ${qualificationResult.reasoning}`);
      }
      
      if (qualificationResult.keyIndicators && qualificationResult.keyIndicators.length > 0) {
        talkingPointsArray.push(`- Key Success Indicators: ${qualificationResult.keyIndicators.join(', ')}`);
      }
      
      if (qualificationResult.concerns && qualificationResult.concerns.length > 0) {
        talkingPointsArray.push(`- Potential Concerns to Address: ${qualificationResult.concerns.join(', ')}`);
      }
    }
    
    // Legacy fit score analysis data (fallback)
    if (websiteData.reasoning) {
      talkingPointsArray.push(`Fit Score Analysis: ${websiteData.reasoning}`);
    }
    if (websiteData.keyIndicators && websiteData.keyIndicators.length > 0) {
      talkingPointsArray.push(`Key Success Indicators: ${websiteData.keyIndicators.join(', ')}`);
    }
    if (websiteData.potentialValue) {
      talkingPointsArray.push(`Potential Value: ${websiteData.potentialValue}`);
    }
    
    const talkingPointsText = talkingPointsArray.length > 0 
      ? talkingPointsArray.map(point => `- ${point}`).join('\n')
      : '- No specific data available';
    
    // Create service-specific structured prompts
    let workflowPrompt;
    
    if (serviceContext.serviceType === 'cold_email') {
      workflowPrompt = `
        <role>
        You're a human writer who naturally connects with readers through authentic, conversational content. You write like you're having a real conversation with someone you genuinely care about helping.
        </role>
        
        <writing_style>
        Use a conversational tone with contractions (don't, can't, we'll, you're)
        Vary sentence length dramatically. Short punchy ones. Then longer, flowing sentences that breathe and give readers time to process what you're sharing with them
        Add natural pauses... like this. And occasional tangents (because that's how real people think)
        Keep language simple - explain things like you would to a friend over coffee
        Use relatable metaphors instead of jargon or AI buzzwords
        </writing_style>
        
        <connection_principles>
        Show you understand what the reader's going through - their frustrations, hopes, and real-world challenges
        Reference the specific context provided and weave in realistic personal experiences that feel authentic to that situation
        Make content slightly "messy" - include small asides, second thoughts, or casual observations
        Connect emotionally first
        Write like you've actually lived through what you're discussing
        Do not use clingy words that a human would not use in a conversation
        </connection_principles>
        
        You are an expert cold email systems consultant. Create a personalized ${style}, ${length} email opener for ${companyName} focused on cold email systems and lead generation.

        PERSONALIZATION TALKING POINTS (use these to show you know them):
        ${talkingPointsText}

        OFFER CONFIGURATION:
        - Template Strategy: ${strategy}
        - Approach Style: ${style}
        - Message Length: ${length}
        - Focus Area: ${serviceContext.focusArea}

        COLD EMAIL SYSTEM FOCUS:
        Focus specifically on how cold email systems can help with ${serviceContext.focusArea}. Use these triggers:
        - Lead Generation Signals: Hiring sales reps or SDRs, growing team size, launching new products, posting about growth targets, mentioning outbound sales challenges
        - Outreach Efficiency Signals: Manual prospecting processes, low response rates, time-consuming lead research, inconsistent messaging, compliance concerns
        - Targeting & Personalization Signals: Poor lead quality issues, generic messaging, lack of segmentation, missing personalization at scale, ineffective follow-up sequences
        - Analytics & Performance Signals: Difficulty tracking campaign performance, lack of visibility into metrics, challenges with attribution, inability to test approaches, missing insights on engagement

        CRITICAL INSTRUCTIONS:
        1. HOOK: Use personalization talking points to create a research-backed hook that shows you know their business
           - Reference specific details from the talking points above
           - Make it conversational and natural with human flow
           - Avoid generic phrases
        
        2. OFFER: Structure your offer based on the configuration:
           - Template Strategy: ${strategy === '8step' ? 'Follow 8-step workflow structure' : 'Use direct approach'}
           - Approach Style: ${style === 'casual' ? 'Keep tone friendly and approachable' : style === 'professional' ? 'Maintain formal business tone' : 'Use confident, direct language'}
           - Message Length: ${length === 'short' ? 'Keep under 3 sentences' : length === 'medium' ? 'Use 4-5 sentences' : 'Allow up to 6-7 sentences'}
           - Focus Area: Focus specifically on ${serviceContext.focusArea} related to cold email systems

        STRUCTURE:
        1. Hook using personalization talking points
        2. Brief pain/opportunity identification related to lead generation or outreach
        3. Specific cold email system offer based on configuration
        4. Social proof or credibility
        5. Low-commitment, value-driven call-to-action

        FORMAT:
          - Reference ${companyName} naturally
          - Use personalization talking points to show research
          - Structure offer according to Template Strategy, Approach Style, Message Length, Focus Area
          - End with value-driven, low-commitment CTA that offers genuine value
          - No greetings or signatures
          - Make it completely specific to this business
          - Write with natural human flow and authentic conversational tone
          - Focus on outcomes and benefits, not features
          - Anchor on their stated goals or public moves
          - Name one friction you can remove (manual work, poor targeting, low response rates)
          
          EXAMPLE CTAs: "I made a video showing how you can do the same - can I shoot it over?", "I put together a quick walkthrough of the cold email system we built - want me to send it?", "I created a simple guide showing the exact process - can I share it?", "I recorded a 2-minute breakdown of how this works - interested?", "I have a case study showing the results - should I send it over?"
          
          OUTPUT ONLY THE EMAIL BODY, nothing else.
      `;
    } else if (serviceContext.serviceType === 'onboarding') {
      const platform = serviceContext.platformName;
      workflowPrompt = `
        <role>
        You're a human writer who naturally connects with readers through authentic, conversational content. You write like you're having a real conversation with someone you genuinely care about helping.
        </role>
        
        <writing_style>
        Use a conversational tone with contractions (don't, can't, we'll, you're)
        Vary sentence length dramatically. Short punchy ones. Then longer, flowing sentences that breathe and give readers time to process what you're sharing with them
        Add natural pauses... like this. And occasional tangents (because that's how real people think)
        Keep language simple - explain things like you would to a friend over coffee
        Use relatable metaphors instead of jargon or AI buzzwords
        </writing_style>
        
        <connection_principles>
        Show you understand what the reader's going through - their frustrations, hopes, and real-world challenges
        Reference the specific context provided and weave in realistic personal experiences that feel authentic to that situation
        Make content slightly "messy" - include small asides, second thoughts, or casual observations
        Connect emotionally first
        Write like you've actually lived through what you're discussing
        Do not use clingy words that a human would not use in a conversation
        </connection_principles>
        
        You are an expert ${platform} consultant. Create a personalized ${style}, ${length} email opener for ${companyName} focused on ${platform} onboarding and implementation.

        PERSONALIZATION TALKING POINTS (use these to show you know them):
        ${talkingPointsText}

        OFFER CONFIGURATION:
        - Template Strategy: ${strategy}
        - Approach Style: ${style}
        - Message Length: ${length}
        - Focus Area: ${serviceContext.focusArea}

        ${platform.toUpperCase()} ONBOARDING FOCUS:
        Focus specifically on how ${platform} can help with ${serviceContext.focusArea}. ${serviceContext.subServiceType === 'apollo' ? 'Use Apollo-specific triggers like prospecting challenges, lead quality issues, manual research processes, CRM integration needs, and sales intelligence gaps.' : 'Use Instantly-specific triggers like cold email deliverability issues, manual outreach processes, scaling challenges, personalization needs, and compliance concerns.'}

        CRITICAL INSTRUCTIONS:
        1. HOOK: Use personalization talking points to create a research-backed hook that shows you know their business
           - Reference specific details from the talking points above
           - Make it conversational and natural with human flow
           - Avoid generic phrases
        
        2. OFFER: Structure your offer based on the configuration:
           - Template Strategy: ${strategy === '8step' ? 'Follow 8-step workflow structure' : 'Use direct approach'}
           - Approach Style: ${style === 'casual' ? 'Keep tone friendly and approachable' : style === 'professional' ? 'Maintain formal business tone' : 'Use confident, direct language'}
           - Message Length: ${length === 'short' ? 'Keep under 3 sentences' : length === 'medium' ? 'Use 4-5 sentences' : 'Allow up to 6-7 sentences'}
           - Focus Area: Focus specifically on ${serviceContext.focusArea} related to ${platform}

        STRUCTURE:
        1. Hook using personalization talking points
        2. Brief pain/opportunity identification related to ${serviceContext.subServiceType === 'apollo' ? 'prospecting or sales intelligence' : 'cold email deliverability or outreach'}
        3. Specific ${platform} offer based on configuration
        4. Social proof or credibility
        5. Low-commitment, value-driven call-to-action

        FORMAT:
          - Reference ${companyName} naturally
          - Use personalization talking points to show research
          - Structure offer according to Template Strategy, Approach Style, Message Length, Focus Area
          - End with value-driven, low-commitment CTA that offers genuine value
          - No greetings or signatures
          - Make it completely specific to this business
          - Write with natural human flow and authentic conversational tone
          - Make "onboarding" concrete: ${serviceContext.subServiceType === 'apollo' ? 'ICP filters → enrichment → sequences → inbox health → routing → CRM mapping → dashboards' : 'domain setup → warmup → sender reputation → sequences → A/B tests → routing → CRM sync → reporting/alerts'}
          - Promise a tiny artifact first: 60-second Loom or 2-bullet plan for their ICP
          - Anchor to outcomes: ${serviceContext.subServiceType === 'apollo' ? 'meetings booked per rep, time saved, cleaner CRM' : 'meetings booked per rep, safe volume, faster ramp, cleaner handoffs'}
          
          EXAMPLE CTAs: "I made a video showing how you can do the same - can I shoot it over?", "I put together a quick walkthrough of the ${platform} setup we built - want me to send it?", "I created a simple guide showing the exact process - can I share it?", "I recorded a 2-minute breakdown of how this works - interested?", "I have a case study showing the results - should I send it over?"
          
          OUTPUT ONLY THE EMAIL BODY, nothing else.
      `;
    } else {
      // Website redesign service (default)
      workflowPrompt = `
        <role>
        You're a human writer who naturally connects with readers through authentic, conversational content. You write like you're having a real conversation with someone you genuinely care about helping.
        </role>
        
        <writing_style>
        Use a conversational tone with contractions (don't, can't, we'll, you're)
        Vary sentence length dramatically. Short punchy ones. Then longer, flowing sentences that breathe and give readers time to process what you're sharing with them
        Add natural pauses... like this. And occasional tangents (because that's how real people think)
        Keep language simple - explain things like you would to a friend over coffee
        Use relatable metaphors instead of jargon or AI buzzwords
        </writing_style>
        
        <connection_principles>
        Show you understand what the reader's going through - their frustrations, hopes, and real-world challenges
        Reference the specific context provided and weave in realistic personal experiences that feel authentic to that situation
        Make content slightly "messy" - include small asides, second thoughts, or casual observations
        Connect emotionally first
        Write like you've actually lived through what you're discussing
        Do not use clingy words that a human would not use in a conversation
        </connection_principles>
        
        You are an expert website redesign consultant. Create a personalized ${style}, ${length} email opener for ${companyName} focused on website improvements and redesign.

        PERSONALIZATION TALKING POINTS (use these to show you know them):
        ${talkingPointsText}

        OFFER CONFIGURATION:
        - Template Strategy: ${strategy}
        - Approach Style: ${style}
        - Message Length: ${length}
        - Focus Area: ${serviceContext.focusArea}

        WEBSITE REDESIGN FOCUS:
        Focus specifically on ${serviceContext.focusArea}. Use these non-visual triggers:
        - Messaging/ICP signals: New ICP or segment push, new positioning statement or value prop, category narrative shift
        - Content/outcome signals: Conversion funnel gaps, unclear CTAs, missing social proof, confusing navigation paths
        - Business context signals: Funding announcements, team growth, product launches, market expansion, competitive moves
        - Performance signals: SEO gaps, mobile issues, page speed problems, accessibility concerns

        CRITICAL INSTRUCTIONS:
        1. HOOK: Use personalization talking points to create a research-backed hook that shows you know their business
           - Reference specific details from the talking points above
           - Make it conversational and natural with human flow
           - Avoid generic phrases
        
        2. OFFER: Structure your offer based on the configuration:
           - Template Strategy: ${strategy === '8step' ? 'Follow 8-step workflow structure' : 'Use direct approach'}
           - Approach Style: ${style === 'casual' ? 'Keep tone friendly and approachable' : style === 'professional' ? 'Maintain formal business tone' : 'Use confident, direct language'}
           - Message Length: ${length === 'short' ? 'Keep under 3 sentences' : length === 'medium' ? 'Use 4-5 sentences' : 'Allow up to 6-7 sentences'}
           - Focus Area: Focus specifically on ${serviceContext.focusArea} related to website improvements

        STRUCTURE:
        1. Hook using personalization talking points
        2. Brief pain/opportunity identification related to website/content issues
        3. Specific website redesign offer based on configuration
        4. Social proof or credibility
        5. Low-commitment, value-driven call-to-action

        FORMAT:
          - Reference ${companyName} naturally
          - Use personalization talking points to show research
          - Structure offer according to Template Strategy, Approach Style, Message Length, Focus Area
          - End with value-driven, low-commitment CTA that offers genuine value
          - No greetings or signatures
          - Make it completely specific to this business
          - Write with natural human flow and authentic conversational tone
          - Focus on content and outcomes, not visual design
          - Anchor on their stated goals or public moves
          - Name one friction you can remove (unclear messaging, poor conversion flow, missing proof)
          
          EXAMPLE CTAs: "I made a video showing how you can do the same - can I shoot it over?", "I put together a quick walkthrough of the website improvements we made - want me to send it?", "I created a simple guide showing the exact process - can I share it?", "I recorded a 2-minute breakdown of how this works - interested?", "I have a case study showing the results - should I send it over?"
          
          OUTPUT ONLY THE EMAIL BODY, nothing else.
      `;
    }
    
    const response = await makeAIRequest(config, workflowPrompt);
    
    if (response.error) {
      throw new Error(`Error generating opener: ${response.error}`);
    }
    
    // Process the response to ensure it meets our requirements
    const opener = processOpenerResponse(response.content, companyName);
    return opener;
  } catch (error) {
    console.error('Error generating structured opener:', error);
    throw new Error(`Error generating opener: ${error.message}`);
  }
};

/**
 * Process the opener response to ensure it meets requirements
 */
const processOpenerResponse = (content, companyName) => {
  // Remove any explanation text that might be present
  let opener = content.replace(/^(Subject|Hook|Pain|Outcome|Proof|CTA):/gi, '');
  
  // Remove any numbering
  opener = opener.replace(/^\d+\.\s+/gm, '');
  
  // Remove any extra annotations or JSON formatting
  opener = opener.replace(/```json|```|"/g, '');
  opener = opener.replace(/\{|\}/g, '');
  
  // Remove any greeting that might have been added
  const greetings = ['Hey', 'Hi', 'Hello', 'Greetings', 'Dear', 'I noticed', 'I saw', 'I was looking'];
  for (const greeting of greetings) {
    const regex = new RegExp(`^${greeting}\\s+(?:${companyName})?[,\\s]`, 'i');
    if (opener.match(regex)) {
      opener = opener.replace(regex, '');
      // Capitalize first letter after removing greeting
      opener = opener.charAt(0).toUpperCase() + opener.slice(1);
    }
  }
  
  // Ensure the opener is properly formatted with good spacing
  opener = opener.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
  
  // Ensure it ends with a question mark
  if (!opener.endsWith('?')) {
    opener += ` Would you like to see a quick example for ${companyName}?`;
  }
  
  return opener;
};

/**
 * Make an API request to the configured AI provider (internal function)
 */
const makeAIRequestInternal = async (config, prompt, retryCount = 0) => {
  try {
    const { endpoint, apiKey, model, ollamaEndpoint, useNoThink } = config;
    
    // Add /nothink suffix if option is enabled
    const finalPrompt = useNoThink ? prompt + "/nothink" : prompt;
    
    console.log('=== AI REQUEST DEBUG ===');
    console.log('Prompt preview:', finalPrompt.substring(0, 300) + '...');
    console.log('Model:', model);
    
    // Handle different API endpoints
    if (endpoint === 'openai' || endpoint === 'openrouter' || endpoint === 'custom') {
      if (!apiKey || !model) {
        return { error: 'API configuration required. Please add your API key and model in Settings.' };
      }
      
      let apiUrl = '';
      let headers = { 'Content-Type': 'application/json' };
      
      // Configure API endpoint based on provider
      switch (endpoint) {
        case 'openai':
          apiUrl = 'https://api.openai.com/v1/chat/completions';
          headers['Authorization'] = `Bearer ${apiKey}`;
          break;
        case 'openrouter':
          apiUrl = 'https://openrouter.ai/api/v1/chat/completions';
          headers['Authorization'] = `Bearer ${apiKey}`;
          headers['HTTP-Referer'] = window.location.origin;
          headers['X-Title'] = 'Website Analyzer';
          break;
        case 'custom':
          apiUrl = 'https://api.openai.com/v1/chat/completions';
          headers['Authorization'] = `Bearer ${apiKey}`;
          break;
      }
      
      // Make the API request
      const requestBody = {
        model: model,
        messages: [
          { role: 'system', content: 'You are a professional, helpful assistant that creates concise, value-first email openers.' },
          { role: 'user', content: finalPrompt }
        ],
        max_tokens: 300,
        temperature: 0.7
      };
      
      const response = await axios.post(apiUrl, requestBody, { headers });
      
      if (response.data && response.data.choices && response.data.choices[0]) {
        let content = response.data.choices[0].message.content.trim();
        
        console.log('AI Response preview:', content.substring(0, 200) + '...');
        console.log('=== END AI REQUEST DEBUG ===');
        
        // Remove empty <think></think> tags if useNoThink is enabled
        if (useNoThink) {
          content = content.replace(/<think>\s*<\/think>/g, '');
          content = content.replace(/<think><\/think>/g, '');
        }
        
        return { content };
      } else {
        return { error: 'Invalid API response structure' };
      }
    } 
    // For Ollama endpoint
    else if (endpoint === 'ollama') {
      if (!ollamaEndpoint || !model) {
        return { error: 'Ollama configuration required. Please add your endpoint URL and model in Settings.' };
      }
      
      // Make the API request to Ollama
      const requestBody = {
        model: model,
        prompt: finalPrompt,
        stream: false
      };
      
      const response = await axios.post(ollamaEndpoint, requestBody);
      
      if (response.data && response.data.response) {
        let content = response.data.response.trim();
        
        // Remove markdown code blocks if present
        content = content.replace(/```json\s*|```\s*$/g, '');
        
        // Remove empty <think></think> tags if useNoThink is enabled
        if (useNoThink) {
          content = content.replace(/<think>\s*<\/think>/g, '');
          content = content.replace(/<think><\/think>/g, '');
        }
        
        return { content };
      } else {
        return { error: 'Invalid Ollama API response structure' };
      }
    } 
    else {
      return { error: 'Unsupported endpoint type: ' + endpoint };
    }
  } catch (error) {
    console.error('API request error:', error);
    
    // Handle rate limiting (429) and server errors (5xx) with retry
    if (error.response && (error.response.status === 429 || error.response.status >= 500)) {
      const maxRetries = 5;
      const baseDelay = 2000; // 2 seconds
      
      if (retryCount < maxRetries) {
        const delay = baseDelay * Math.pow(2, retryCount); // Exponential backoff
        console.log(`Rate limited or server error. Retrying in ${delay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return makeAIRequest(config, prompt, retryCount + 1);
      } else {
        return { error: `Rate limit exceeded. Please wait a moment and try again. (${error.response.status})` };
      }
    }
    
    return { error: error.message || 'Unknown error' };
  }
};

/**
 * Wrapper for makeAIRequest with rate limiting
 */
const makeAIRequest = async (config, prompt) => {
  const apiCall = async () => {
    return await makeAIRequestInternal(config, prompt, 0);
  };
  
  try {
    return await executeSequentialWithRetry(apiCall);
  } catch (error) {
    console.error('Sequential AI request failed after retries:', error.message);
    return { error: error.message || 'Sequential AI request failed after multiple attempts' };
  }
};

/**
 * Regenerate opener for a specific website
 * @param {Object} website - Website data
 * @param {Object} config - API configuration
 * @param {Object} openerConfig - Optional opener generation settings
 * @returns {Promise<Object>} Result of the operation
 */
export const regenerateSequentialOpener = async (website, config, openerConfig = null) => {
  try {
    console.log('=== REGENERATION DEBUG ===');
    console.log('Starting sequential opener regeneration for URL:', website.url);
    console.log('Website company:', website.company);
    console.log('Website title:', website.title);
    console.log('Website content preview:', website.content ? website.content.substring(0, 200) + '...' : 'No content');
    console.log('Opener settings:', openerConfig);
    
    // Create a copy of the website data to ensure we have all required fields
    const websiteData = {
      ...website,
      company: website.company || extractDomain(website.url),
      fitScore: website.fitScore || 5
    };
    
    // Log the service context for debugging
    const serviceContext = getServiceContext(
      openerConfig?.serviceType || 'website',
      openerConfig?.subServiceType,
      openerConfig?.focus || 'value',
      openerConfig?.manualFocus
    );
    
    console.log('Service context for', website.url, ':', serviceContext);

    // Generate new opener using sequential approach
    const newOpener = await generateSequentialOpener(
      websiteData,
      config,
      openerConfig
    );
    
    console.log('Generated new sequential opener for', website.url, ':', newOpener.substring(0, 100) + '...');
    console.log('=== END REGENERATION DEBUG ===');

    // Check if opener contains error message
    if (newOpener.startsWith('[Error') || newOpener.startsWith('[API configuration')) {
      return {
        success: false,
        error: newOpener.replace(/[[\]]/g, '')
      };
    }

    // Note: localStorage update is handled by the calling component to avoid conflicts

    return {
      success: true,
      opener: newOpener,
      serviceType: openerConfig?.serviceType || 'website',
      subServiceType: openerConfig?.subServiceType || null
    };
  } catch (error) {
    console.error('Error regenerating sequential opener:', error);
    return {
      success: false,
      error: error.message || 'Failed to regenerate opener'
    };
  }
};